import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip
} from '@mui/material';


const ProductCard = ({ 
  product, 
  onClick, 
  showColors = true,
  variant = 'default' // 'default', 'compact', 'hero'
}) => {

  const {
    name,
    price,
    description,
    reference,
    displayReference,
    mainImage,
    colors = [],
    priceInRub,
    futurePrice
  } = product;

  const formatPrice = (value) => {
    if (value === undefined || value === null) return '';
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Определяем отображаемую цену
  const displayPrice = priceInRub || price;
  
  // Проверяем наличие скидки
  const hasDiscount = futurePrice && futurePrice.price;
  const discountPercentage = hasDiscount ? futurePrice.displayDiscountPercentage || futurePrice.discountPercentage : null;
  const discountPrice = hasDiscount ? futurePrice.price : null;
  const discountLabel = hasDiscount
    ? futurePrice.discountLabel ||
      (discountPercentage ? `-${String(discountPercentage).replace('%', '')}%` : null)
    : null;
  const discountStartsAt = hasDiscount && futurePrice.description ? futurePrice.description.trim() : null;

  // Функция для обрезки названия товара максимум в 1 строку
  const truncateProductName = (productName, maxChars = 35) => {
    if (!productName) return '';
    
    if (productName.length <= maxChars) {
      return productName;
    }
    
    // Обрезаем название и добавляем "+"
    return productName.substring(0, maxChars - 1) + '+';
  };

  // Обрабатываем название товара
  const processedName = truncateProductName(name);

  // Определяем размеры в зависимости от варианта и размера экрана
  // Все размеры увеличены в 1.4 раза для лучшего отображения товаров
  const getCardStyles = () => {
    switch (variant) {
      case 'compact':
        return {
          imageHeight: Math.round(200 * 1.4), // 280px (было 200px)
          titleFontSize: '0.75rem', // Уменьшаем размер шрифта
          descriptionFontSize: '0.65rem', // Уменьшаем размер шрифта
          // Убираем фиксированную высоту для автоматической регулировки
          minHeight: Math.round(220 * 1.4), // 308px минимальная высота
        };
      case 'hero':
        return {
          imageHeight: Math.round(400 * 1.4), // 560px (было 400px)
          titleFontSize: '0.9rem', // Уменьшаем размер шрифта
          descriptionFontSize: '0.75rem', // Уменьшаем размер шрифта
          // Hero карточки могут быть выше
          minHeight: Math.round(350 * 1.4), // 490px минимальная высота
        };
      default:
        return {
          imageHeight: Math.round(320 * 1.4), // 448px (было 320px)
          titleFontSize: '0.8rem', // Уменьшаем размер шрифта
          descriptionFontSize: '0.7rem', // Уменьшаем размер шрифта
          // Убираем фиксированную высоту для автоматической регулировки
          minHeight: Math.round(280 * 1.4), // 392px минимальная высота
        };
    }
  };

  const styles = getCardStyles();

  // Функция для определения цвета по названию
  const getColorByName = (colorName) => {
    if (!colorName) return '#ccc';
    
    const colorNameLower = colorName.toLowerCase();
    if (colorNameLower.includes('белый') || colorNameLower.includes('white')) {
      return '#ffffff';
    } else if (colorNameLower.includes('черный') || colorNameLower.includes('black')) {
      return '#000000';
    } else if (colorNameLower.includes('красный') || colorNameLower.includes('red')) {
      return '#ff0000';
    } else if (colorNameLower.includes('синий') || colorNameLower.includes('blue')) {
      return '#0000ff';
    } else if (colorNameLower.includes('зеленый') || colorNameLower.includes('green')) {
      return '#00ff00';
    } else if (colorNameLower.includes('желтый') || colorNameLower.includes('yellow')) {
      return '#ffff00';
    } else if (colorNameLower.includes('розовый') || colorNameLower.includes('pink')) {
      return '#ffc0cb';
    } else if (colorNameLower.includes('оранжевый') || colorNameLower.includes('orange')) {
      return '#ffa500';
    } else if (colorNameLower.includes('фиолетовый') || colorNameLower.includes('purple')) {
      return '#800080';
    } else if (colorNameLower.includes('коричневый') || colorNameLower.includes('brown')) {
      return '#a52a2a';
    } else if (colorNameLower.includes('серый') || colorNameLower.includes('gray') || colorNameLower.includes('grey')) {
      return '#808080';
    } else if (colorNameLower.includes('бежевый') || colorNameLower.includes('beige')) {
      return '#f5f5dc';
    } else if (colorNameLower.includes('голубой') || colorNameLower.includes('cyan')) {
      return '#00ffff';
    }
    return '#ccc';
  };


  return (
    <Card
      onClick={onClick}
      className={`product-card ${variant === 'hero' ? 'hero-card' : ''}`}
      elevation={0} // Убираем стандартную тень Material-UI
      sx={{ 
        minHeight: styles.minHeight, // Используем minHeight для автоматической регулировки
        width: '100%', // Карточка занимает всю доступную ширину в своей ячейке
        cursor: onClick ? 'pointer' : 'default',
        border: 'none',
        backgroundColor: 'transparent', // Убираем белый фон
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: onClick ? 'translateY(-4px)' : 'none',
          boxShadow: onClick ? '0 8px 25px rgba(0,0,0,0.15)' : 'none'
        }
      }}
    >
      <CardContent sx={{ 
        p: 2, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: 'transparent' // Убираем белый фон у содержимого
      }}>
        {/* Изображение товара */}
        <Box
          sx={{
            width: '100%',
            height: styles.imageHeight,
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
            mb: 1,
            // Адаптивные стили для изображений
            '@media (max-width: 768px)': {
              height: Math.round(styles.imageHeight * 0.7), // Уменьшаем высоту на мобильных
              mb: 0.5
            },
            '@media (max-width: 480px)': {
              height: Math.round(styles.imageHeight * 0.6), // Еще больше уменьшаем на маленьких экранах
              borderRadius: 0.5
            }
          }}
        >
          <img
            src={mainImage}
            alt={name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (onClick) e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          />
          

        </Box>
        
        {/* Информация о товаре */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box>
            {/* Название товара максимум в 1 строку */}
            <Box sx={{ mb: 1, minHeight: '1.2em' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 500,
                  fontSize: styles.titleFontSize,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {processedName}
              </Typography>
            </Box>
            
       
            
            {/* Артикул с уменьшенным размером */}
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                mb: showColors && colors && colors.length > 1 ? 
                  { xs: 0.5, sm: 1 } : // На мобилке меньше, на планшете больше
                  { xs: 0.25, sm: 0.5 }, // Еще меньше когда нет цветов
                fontSize: '0.65rem', // Уменьшаем размер шрифта
                opacity: 0.7
              }}
            >
              Артикул: {displayReference || reference || 'Н/Д'}
            </Typography>
            
            {/* Цвета - показываем только если их больше одного */}
            {showColors && colors && colors.length > 1 && (
              <Box sx={{ mb: { xs: 0.5, sm: 1 } }}>
                           
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {colors.slice(0, 6).map((color, index) => {
                    const displayColor = color.hexCode || getColorByName(color.name);
                    const colorName = color.name || 'Неизвестный';
                    
                    return (
                      <Box
                        key={color.id || index}
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          backgroundColor: displayColor,
                          border: displayColor === '#ffffff' ? '1px solid #ddd' : 'none',
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.2)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            zIndex: 1
                          }
                        }}
                        title={`${colorName}${color.hexCode ? ` (${color.hexCode})` : ''}`}
                      />
                    );
                  })}
                  {colors.length > 6 && (
                    <Typography variant="caption" color="text.secondary">
                      +{colors.length - 6}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
          
          {/* Цена и кнопка избранного на одной строке */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {hasDiscount ? (
                <>
                  {/* Цена со скидкой */}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'error.main',
                      fontSize: '0.9rem',
                      lineHeight: 1.2
                    }}
                  >
                    {formatPrice(discountPrice)}
                  </Typography>
                  {/* Старая цена и процент скидки */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        textDecoration: 'line-through',
                        fontSize: '0.75rem'
                      }}
                    >
                      {formatPrice(displayPrice)}
                    </Typography>
                    <Chip 
                      label={discountLabel || '-%'}
                      size="small"
                      color="error"
                      sx={{ 
                        height: 20,
                        fontSize: '0.6rem',
                        fontWeight: 600
                      }}
                    />
                  </Box>
                  {discountStartsAt && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.25, fontSize: '0.7rem' }}
                    >
                      {discountStartsAt}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'primary.main',
                    fontSize: '0.9rem'
                  }}
                >
                  {formatPrice(displayPrice)}
                </Typography>
              )}
            </Box>
            

          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
